import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'language/translator'

import { TWidgetSettingsLoaded, WidgetType } from 'types/GlobalWidget'

import { AiIconTip } from 'components_new/molecules/IconTip'
import SettingsDrawer from 'components_new/molecules/SettingsDrawer'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'

import { getOptions } from './utils'
import { AdditionalSegment } from 'types/GlobalAdditionalSegment'
import {
  useAttributeOptions,
  useCommonAttributeOptions
} from 'redux/hooks/AttributeOptions'

interface AdditionalSegmentSectionProps {
  createAdditionalSegmentBy: (attributeOptionId: string) => void
  deleteAdditionalSegmentBy: (additionalSegmentId: string) => void
  onClose: () => void
  updateAdditionalSegmentBy: (
    additionalSegmentId: string,
    attributeOptionId: string
  ) => void
  widget: TWidgetSettingsLoaded
}

const LEGAL_TYPES = [WidgetType.LIST]

const AdditionalSegmentSection = (props: AdditionalSegmentSectionProps) => {
  const {
    createAdditionalSegmentBy,
    deleteAdditionalSegmentBy,
    onClose,
    updateAdditionalSegmentBy,
    widget
  } = props

  const { t } = useTranslation()
  const attributeOptions = useAttributeOptions()
  const segmentAttributeOptions = useCommonAttributeOptions(
    widget,
    'segment_attribute'
  )
  const [drawer, setDrawer] = useState<{
    open: boolean
    additionalSegmentId: string | null
    attributeOptionId: string | null
  }>({
    open: false,
    additionalSegmentId: null,
    attributeOptionId: null
  })
  const [dialog, setDialog] = useState<{
    open: boolean
    title: string
    variant: 'add' | 'edit'
    data: {
      value: string
    }
  }>({
    open: false,
    variant: 'add',
    title: '',
    data: {
      value: ''
    }
  })

  useEffect(() => {
    if (drawer.additionalSegmentId) {
      const additionalSegment = widget.settings.segment_by.additional.find(
        (additional) => additional.id === drawer.additionalSegmentId
      ) as AdditionalSegment

      setDrawer({
        ...drawer,
        attributeOptionId: additionalSegment.attribute_option_id
      })
    }
  }, [widget.settings.segment_by.additional])

  const drawerAttributeOption = useMemo(
    () =>
      drawer.attributeOptionId
        ? attributeOptions[drawer.attributeOptionId]
        : null,
    [drawer.attributeOptionId, attributeOptions]
  )

  if (
    !LEGAL_TYPES.includes(widget.settings.type.selected) ||
    !widget.settings.segment_by.allow_additional_segments
  ) {
    return null
  }

  /*-- drawer --*/
  const handleBackDrawer = () => {
    setDrawer((prevState) => ({
      ...prevState,
      additionalSegmentId: null,
      open: false
    }))
  }

  const handleCloseDrawer = () => {
    handleBackDrawer()
    onClose()
  }

  const handleOpenDrawer = (id: string) => {
    const additionalSegment = widget.settings.segment_by.additional.find(
      (additional) => additional.id === id
    ) as AdditionalSegment

    setDrawer({
      open: true,
      additionalSegmentId: id,
      attributeOptionId: additionalSegment.attribute_option_id
    })
  }

  /*-- dialogs --*/
  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleOpenDialog = (variant: 'add' | 'edit') => {
    let inputValue = ''

    if (variant === 'edit') {
      inputValue = drawerAttributeOption?.id ?? ''
    }

    setDialog({
      open: true,
      title: t('widgets', 'additional_segmentation_title'),
      variant: variant,
      data: {
        value: inputValue
      }
    })
  }

  const handleEditDialogValue = (value: string) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { ...prevState.data, value }
    }))
  }

  const handleSubmitDialog = () => {
    switch (dialog.variant) {
    case 'add':
      createAdditionalSegmentBy(dialog.data.value)
      break
    case 'edit':
      if (drawer.additionalSegmentId) {
        updateAdditionalSegmentBy(
          drawer.additionalSegmentId,
          dialog.data.value
        )
      }
      break
    }
  }

  const handleDelete = () => {
    if (drawer.additionalSegmentId) {
      deleteAdditionalSegmentBy(drawer.additionalSegmentId)
      handleBackDrawer()
    }
  }

  return (
    <>
      <SettingsGroup
        helperText={t('widgets', 'additional_segmentation_helper')}
        title={t('widgets', 'additional_segmentation_title')}
      >
        {widget.settings.segment_by.additional.map((additional, i) => {
          const label =
            attributeOptions[additional.attribute_option_id]?.name ?? ''
          const aiGenerated =
            !!attributeOptions[additional.attribute_option_id]?.ai_generated

          return (
            <SettingsItem
              indent={2 * (i + 1)}
              key={additional.id}
              onClick={() => handleOpenDrawer(additional.id)}
              title={label}
              titleAdornments={
                aiGenerated ? (
                  <AiIconTip title={t('global', 'ai_generated')} />
                ) : undefined
              }
            />
          )
        })}
        {widget.settings.segment_by.additional.length < 3 ? (
          <SettingsItem
            disabled={widget.settings.is_preview}
            disabledText={t(
              'widgets',
              'disabled_additional_segmentation_preview'
            )}
            color="success"
            indent={2 * (widget.settings.segment_by.additional.length + 1)}
            onClick={() => handleOpenDialog('add')}
            title={t('global', 'add')}
            variant="add"
          />
        ) : null}
      </SettingsGroup>

      {/*-- drawer --*/}
      <SettingsDrawer
        open={drawer.open}
        onBack={handleBackDrawer}
        onClose={handleCloseDrawer}
        title={t('widgets', 'additional_segmentation_title')}
      >
        {drawerAttributeOption ? (
          <>
            <SettingsGroup>
              <SettingsItem
                onClick={() => handleOpenDialog('edit')}
                title={t('widgets', 'segment_by_title')}
                value={drawerAttributeOption.name}
                valueAdornments={
                  drawerAttributeOption?.ai_generated ? (
                    <AiIconTip title={t('global', 'ai_generated')} />
                  ) : undefined
                }
                variant="edit"
              />
            </SettingsGroup>

            <SettingsGroup>
              <SettingsItem
                color="error"
                onClick={handleDelete}
                title={t('global', 'delete')}
                variant="action"
              />
            </SettingsGroup>
          </>
        ) : null}
      </SettingsDrawer>

      {/*-- dialogs --*/}
      <SettingsEditDialog
        onChange={handleEditDialogValue}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        options={getOptions(
          widget.settings,
          segmentAttributeOptions,
          t('global', 'ai_generated')
        )}
        title={dialog.title}
        type={'select'}
        value={dialog.data.value}
        variant={dialog.variant}
      />
    </>
  )
}

export default AdditionalSegmentSection
